import { useTranslation } from 'react-i18next'
import { useLocale } from '../../contexts/LocaleContext'
import { useFormatters } from '../../lib/format'
import { Skeleton } from '../ui/skeleton'
import { useGroupGovernance, type Period } from '../../api/groupSteering'
import { langOf, text } from './format'
import { ExceptionsTable } from './PerimetersView'
import { Section } from './primitives'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'

/**
 * Governance — the group profile's work view: the ticker of active
 * exceptions, the three money and deadline cards with the euro figure
 * leading, the exceptions table, the deadlines, the reconciliation of the
 * two ways of counting, and who holds the console.
 */
export function GovernanceView({ org, period }: { org: string; period: Period }) {
    const { t } = useTranslation('group-steering')
    const { locale } = useLocale()
    const lang = langOf(locale)
    const { formatDate, formatCurrency, formatMoney } = useFormatters()
    const gov = useGroupGovernance(org, period)
    if (gov.isLoading) return <Skeleton className="h-64 w-full" />
    if (!gov.data) return null
    const g = gov.data
    const money = (cents: number) => formatCurrency(cents / 100, g.money.currency || 'EUR')

    return (
        <div className="space-y-4" data-testid="group-governance">
            <PageHeader
                icon={SECTION_ICONS.compliance}
                title={t('governance.title')}
                description={t('governance.intro')}
                data-testid="group-governance-title"
            />

            <div className="rounded-lg border bg-muted/30 px-3 py-2 overflow-hidden" data-testid="governance-ticker">
                <div className="text-[11px] text-muted-foreground">
                    {t('governance.ticker', { count: g.exceptions.length, date: g.freshness.computed_at ? formatDate(g.freshness.computed_at, { dateStyle: 'short', timeStyle: 'short' }) : '—' })}
                </div>
                <div className="mt-1 flex gap-6 whitespace-nowrap text-sm overflow-x-auto">
                    {g.ticker.map((e, i) => (
                        <span key={i} className="text-foreground">
                            <span className="font-mono text-xs text-muted-foreground">{e.kpi}</span> {e.cell.value === null || e.cell.value === undefined ? '—' : Math.round(e.cell.value * 100) / 100} — {e.perimeter.name}
                        </span>
                    ))}
                    {g.ticker.length === 0 && <span className="text-muted-foreground">{t('exceptions.empty')}</span>}
                </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3" data-testid="governance-money">
                <div className="rounded-lg border bg-card p-4">
                    <div className="text-xs text-muted-foreground">{t('governance.supplier_debt')}</div>
                    <div className="text-2xl font-semibold tabular-nums text-foreground" data-testid="money-supplier-debt">{money(g.money.supplier_unsettled_aged_cents)}</div>
                    <div className="text-[11px] text-muted-foreground">{t('governance.supplier_debt_desc', { count: g.money.supplier_unsettled_lines })}</div>
                    {g.money.supplier_unsettled_by_currency.length > 1 && (
                        <div className="mt-1 text-[11px] text-muted-foreground" data-testid="money-supplier-debt-other-currencies">
                            {t('governance.supplier_debt_other_currencies')}{' '}
                            {g.money.supplier_unsettled_by_currency
                                .slice(1)
                                .map((l) => `${formatCurrency(l.cents / 100, l.currency || 'EUR')} (${l.lines})`)
                                .join(' · ')}
                        </div>
                    )}
                </div>
                <div className="rounded-lg border bg-card p-4">
                    <div className="text-xs text-muted-foreground">{t('governance.compliance_due')}</div>
                    <div className="text-2xl font-semibold tabular-nums text-foreground" data-testid="money-compliance-due">{g.money.compliance_due_90d}</div>
                    <div className="text-[11px] text-muted-foreground">{t('governance.compliance_due_desc', { count: g.money.compliance_overdue })}</div>
                </div>
                <RentAtRiskCard money={g.money} formatMoney={formatMoney} formatDate={formatDate} />
            </div>

            <Section id="governance-exceptions" title={t('exceptions.title')} description={t('governance.intro')}>
                <ExceptionsTable rows={g.exceptions} org={org} />
            </Section>

            <Section id="governance-deadlines" title={t('governance.deadlines_title')} description="" defaultOpen={false}>
                <ul className="text-sm space-y-1" data-testid="governance-deadlines">
                    <li><span className="font-medium tabular-nums text-foreground">{g.deadlines.contracts_expiring_90d}</span> <span className="text-muted-foreground">{t('governance.contracts_expiring')}</span></li>
                    <li><span className="font-medium tabular-nums text-foreground">{g.deadlines.evaluations_due_90d}</span> <span className="text-muted-foreground">{t('governance.evaluations_due')}</span></li>
                </ul>
            </Section>

            <Section id="governance-perimeter" title={t('governance.perimeter_title')} description="">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm" data-testid="governance-perimeter">
                    {(['entities', 'agencies', 'portfolios', 'sites', 'units', 'unassigned'] as const).map((k) => (
                        <div key={k} className="rounded-md border p-2">
                            <div className="text-lg font-semibold tabular-nums text-foreground">{k === 'unassigned' ? g.perimeter.sites_unassigned : g.perimeter[k]}</div>
                            <div className="text-[11px] text-muted-foreground">{t(`governance.${k}`)}</div>
                        </div>
                    ))}
                </div>
                <p className="text-sm text-muted-foreground" data-testid="governance-reconciliation">{text(g.perimeter.reconciliation, lang)}</p>
            </Section>

            <Section id="governance-access" title={t('governance.access_title')} description="" defaultOpen={false}>
                {g.access.length === 0 ? (
                    <EmptyState variant="inline" title={t('governance.access_empty')} />
                ) : (
                    <table className="w-full text-sm" data-testid="governance-access">
                        <thead className="text-xs text-muted-foreground">
                            <tr className="text-left">
                                <th className="py-1 pr-2 font-medium">{t('governance.access_role')}</th>
                                <th className="py-1 pr-2 font-medium text-right">{t('governance.access_members')}</th>
                                <th className="py-1 font-medium text-right">{t('governance.access_scoped')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {g.access.map((a) => (
                                <tr key={a.role} className="border-t">
                                    <td className="py-1 pr-2 font-mono text-xs">{a.role}</td>
                                    <td className="py-1 pr-2 text-right tabular-nums">{a.members}</td>
                                    <td className="py-1 text-right tabular-nums">{a.scoped}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Section>
        </div>
    )
}

type GovernanceMoney = NonNullable<ReturnType<typeof useGroupGovernance>['data']>['money']

/**
 * Rents at risk: the annual rent of the leases that breach a habitability rule at
 * the next deadline the rules set, each amount in its own currency. The card
 * says what the total leaves out (leases whose rent could not be annualised)
 * and which three perimeters carry most of it.
 */
function RentAtRiskCard({
    money: m,
    formatMoney,
    formatDate,
}: {
    money: GovernanceMoney
    formatMoney: (minorUnits: number | null | undefined, currency?: string | null) => string
    formatDate: (date: Date | string, options?: Intl.DateTimeFormatOptions) => string
}) {
    const { t } = useTranslation('group-steering')
    const held = m.rent_at_risk_annual_cents !== undefined
    // A date-only value: read it in UTC so no time zone moves it a day.
    const at = m.rent_at_risk_at ? formatDate(m.rent_at_risk_at, { dateStyle: 'long', timeZone: 'UTC' }) : ''

    return (
        <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-4" data-testid="money-rent-at-risk-card">
            <div className="text-xs text-muted-foreground">{t('governance.rent_at_risk')}</div>
            <div className="text-2xl font-semibold tabular-nums text-foreground" data-testid="money-rent-at-risk">
                {held ? formatMoney(m.rent_at_risk_annual_cents, m.rent_at_risk_currency) : '—'}
            </div>
            {!held ? (
                <div className="text-[11px] text-muted-foreground" data-testid="money-rent-at-risk-none">{t('governance.rent_at_risk_none')}</div>
            ) : (
                <>
                    <div className="text-[11px] text-muted-foreground" data-testid="money-rent-at-risk-at">{t('governance.rent_at_risk_desc', { date: at })}</div>
                    <div className="text-[11px] text-muted-foreground" data-testid="money-rent-at-risk-leases">
                        {t('governance.rent_at_risk_leases', { count: m.rent_at_risk_leases })}
                    </div>
                    {m.rent_at_risk_unpriced_leases > 0 && (
                        <div className="text-[11px] text-warning" data-testid="money-rent-at-risk-unpriced">
                            {t('governance.rent_at_risk_unpriced', { count: m.rent_at_risk_unpriced_leases })}
                        </div>
                    )}
                    {m.rent_at_risk_by_currency.length > 1 && (
                        <div className="mt-1 text-[11px] text-muted-foreground" data-testid="money-rent-at-risk-other-currencies">
                            {t('governance.supplier_debt_other_currencies')}{' '}
                            {m.rent_at_risk_by_currency
                                .slice(1)
                                .map((l) => `${formatMoney(l.cents, l.currency)} (${l.lines})`)
                                .join(' · ')}
                        </div>
                    )}
                    {m.rent_at_risk_top.length > 0 && (
                        <div className="mt-2" data-testid="money-rent-at-risk-top">
                            <div className="text-[11px] text-muted-foreground" data-testid="money-rent-at-risk-top-title">{t('governance.rent_at_risk_top')}</div>
                            <ol className="mt-0.5 space-y-0.5 text-xs" data-testid="money-rent-at-risk-top-list">
                                {m.rent_at_risk_top.map((line, i) => (
                                    <li
                                        key={`${line.perimeter.uid ?? i}-${line.currency}`}
                                        className="flex justify-between gap-2"
                                        data-testid={`money-rent-at-risk-top-${i}`}
                                    >
                                        <span className="truncate text-foreground" data-testid={`money-rent-at-risk-top-${i}-name`}>{line.perimeter.name}</span>
                                        <span className="tabular-nums text-foreground" data-testid={`money-rent-at-risk-top-${i}-amount`}>
                                            {formatMoney(line.cents, line.currency)}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
